import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { stationKey, type Station } from "./m3u";

const STORAGE_KEY = "raydeeyoo:starred";

type StarredContextValue = {
  starred: Station[];
  isStarred: (station: Station) => boolean;
  toggleStar: (station: Station) => void;
};

const StarredContext = createContext<StarredContextValue | null>(null);

function isStation(value: unknown): value is Station {
  const station = value as Station;
  return (
    typeof station === "object" &&
    station !== null &&
    typeof station.name === "string" &&
    typeof station.url === "string" &&
    Array.isArray(station.genres)
  );
}

function readStarred(): Station[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter(isStation) : [];
  } catch {
    return [];
  }
}

function writeStarred(stations: Station[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stations));
  } catch {
    // Storage can be unavailable (private mode, quota); starring still works for this session.
  }
}

export function StarredProvider({ children }: { children: ReactNode }) {
  const [starred, setStarred] = useState<Station[]>(readStarred);

  // Keep other open tabs in sync.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setStarred(readStarred());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const keys = useMemo(() => new Set(starred.map(stationKey)), [starred]);
  const isStarred = useCallback((station: Station) => keys.has(stationKey(station)), [keys]);

  const toggleStar = useCallback((station: Station) => {
    setStarred((current) => {
      const key = stationKey(station);
      const { name, url, logo, genres } = station;
      const next = current.some((s) => stationKey(s) === key)
        ? current.filter((s) => stationKey(s) !== key)
        : [{ name, url, logo, genres }, ...current];
      writeStarred(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ starred, isStarred, toggleStar }), [starred, isStarred, toggleStar]);
  return <StarredContext.Provider value={value}>{children}</StarredContext.Provider>;
}

export function useStarred(): StarredContextValue {
  const context = useContext(StarredContext);
  if (!context) throw new Error("useStarred must be used inside StarredProvider");
  return context;
}
